import type { DocBlock, DocCostTable, DocRun, OfferDocument } from '../types';

/**
 * Podgląd pisma ofertowego.
 *
 * Rysuje bloki przysłane przez serwer (`OfferDto.documentView` albo `POST /api/offers/preview`)
 * — te same, z których powstaje PDF i DOCX. Komponent NIE składa własnych zdań, nie formatuje
 * dat ani kwot i nie decyduje, co się pokazuje: gdyby zaczął, wróciłby rozjazd „podgląd inny
 * niż pobrany plik”, dla którego powstał ten model (V160).
 *
 * Krój jak w DOCX (Century Gothic); bez Office'a w systemie przeglądarka weźmie zastępczy.
 */
const LETTER_FONT = "'Century Gothic', 'URW Gothic', 'Avant Garde', sans-serif";

export function OfferDocumentView({ doc }: { doc: OfferDocument }) {
  const twoSignatureLines = doc.signatureLabels.length === 2;
  return (
    <div className="bg-white p-8 shadow rounded text-[14px] text-ink-900 max-w-[680px] mx-auto"
         style={{ fontFamily: LETTER_FONT }}>
      {doc.header && (
        <div className="border-b border-ink-300 pb-3 mb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 leading-tight">
              <div className="font-bold text-[13px]">{doc.header.fullName}</div>
              {doc.header.addressLine && <div className="text-[11px] text-ink-700">{doc.header.addressLine}</div>}
              {doc.header.legalLine && <div className="text-[11px] text-ink-700">{doc.header.legalLine}</div>}
            </div>
            {doc.header.logoUrl && (
              <img src={doc.header.logoUrl} alt="" className="h-12 max-w-[130px] object-contain"/>
            )}
          </div>
        </div>
      )}

      <div className="text-right text-[12px] mb-6">{doc.dateLine}</div>

      {doc.blocks.map((b, i) => <Block key={i} b={b}/>)}

      {doc.signatureUrl && !twoSignatureLines && (
        <img src={doc.signatureUrl} alt="Pieczątka z podpisem"
             className="mt-1 max-h-[60px] max-w-[200px] object-contain object-left"/>
      )}

      {/* Przy dwóch liniach pieczątka stoi nad prawą („do wystawienia”) — lewa jest klienta. */}
      {twoSignatureLines && (
        <div className={`${doc.signatureUrl ? 'mt-1' : 'mt-10'} grid grid-cols-2 gap-6 text-center items-end`}>
          {doc.signatureLabels.map((label, i) => (
            <div key={i}>
              {i === 1 && doc.signatureUrl && (
                <img src={doc.signatureUrl} alt="Pieczątka z podpisem"
                     className="mx-auto mb-0.5 max-h-[60px] max-w-[180px] object-contain"/>
              )}
              <div className="border-t border-ink-400 mx-6"/>
              <div className="text-[11px] italic text-ink-600 mt-1">{label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Block({ b }: { b: DocBlock }) {
  const align = b.align === 'CENTER' ? 'text-center' : b.align === 'RIGHT' ? 'text-right' : 'text-left';

  switch (b.kind) {
    case 'PARAGRAPH':
      // Pusty akapit z edytora musi zająć wiersz — tak samo jak w PDF i DOCX.
      if (b.runs.length === 0) return <div className="h-[1.4em]"/>;
      return (
        <p className={`mb-2 ${align}`} style={{ fontSize: `${14 * b.scale}px` }}>
          <Runs runs={b.runs}/>
        </p>
      );

    case 'HEADING':
      return (
        <div className={`font-semibold mt-3 mb-1 ${align}`} style={{ fontSize: b.level <= 2 ? '16px' : '14px' }}>
          <Runs runs={b.runs}/>
        </div>
      );

    case 'LIST':
      return (
        <div className="mb-2">
          {b.items.map((it, i) => (
            <div key={i} className="flex gap-2 text-[13px] text-justify" style={{ paddingLeft: `${(it.level + 1) * 14}px` }}>
              <span className="shrink-0">{it.marker}</span>
              <span><Runs runs={it.runs}/></span>
            </div>
          ))}
        </div>
      );

    case 'SPEC_TABLE':
      // Kolumna kluczy szeroka na najdłuższy klucz — jak w PDF i DOCX („SW2” nie zabiera 1/3 wiersza).
      return (
        <table className="text-[12px] w-full border-collapse mb-3">
          <tbody>
            {b.rows.map((r, i) => (
              <tr key={i}>
                <td className="border border-ink-200 bg-ink-50 px-2 py-0.5 font-medium w-[1%] whitespace-nowrap align-top">{r.key}</td>
                <td className="border border-ink-200 px-2 py-0.5 align-top">{r.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );

    case 'COST_TABLE':
      return b.costTable ? <CostTable t={b.costTable}/> : null;

    case 'RULE':
      return <hr className="my-3 border-ink-300"/>;

    case 'SPACER':
      return <div style={{ height: `${b.scale * 1.4}em` }}/>;

    default:
      return null;
  }
}

/** Tabela kosztorysu — układ jak w PDF i DOCX: ramki, kwoty do prawej, wyróżniony ostatni wiersz. */
function CostTable({ t }: { t: DocCostTable }) {
  const n = t.columns.length;
  const cell = 'border border-ink-200 px-2 py-0.5 align-top';
  const num = (i: number) => (t.columns[i]?.numeric ? 'text-right tabular-nums whitespace-nowrap' : '');
  return (
    <table className="text-[12px] w-full border-collapse mb-3 table-fixed">
      <colgroup>
        {t.columns.map((c, i) => <col key={i} style={{ width: `${c.width * 100}%` }}/>)}
      </colgroup>
      <thead>
        <tr className="bg-ink-50">
          {t.columns.map((c, i) => <th key={i} className={`${cell} font-semibold ${c.numeric ? 'text-right' : 'text-left'}`}>{c.title}</th>)}
        </tr>
      </thead>
      <tbody>
        {t.rows.map((r, k) => {
          if (r.kind === 'ITEM') {
            return <tr key={k}>{r.cells.map((v, i) => <td key={i} className={`${cell} ${num(i)}`}>{v}</td>)}</tr>;
          }
          if (r.kind === 'GROUP') {
            return <tr key={k} className="bg-ink-50/50"><td colSpan={n} className={`${cell} font-semibold`}>{r.cells[0]}</td></tr>;
          }
          const grand = r.kind === 'GRAND_TOTAL';
          return (
            <tr key={k} className={grand ? 'bg-ink-50 font-semibold' : ''}>
              <td colSpan={n - 1} className={`${cell} text-right`}>{r.cells[0]}</td>
              <td className={`${cell} text-right tabular-nums whitespace-nowrap`}>{r.cells[1]}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Runs({ runs }: { runs: DocRun[] }) {
  return (
    <>
      {runs.map((r, i) => {
        if (r.lineBreak) return <br key={i}/>;
        const cls = [
          r.bold ? 'font-bold' : '',
          r.italic ? 'italic' : '',
          r.strike ? 'line-through' : '',
        ].filter(Boolean).join(' ');
        return <span key={i} className={cls || undefined}>{r.text}</span>;
      })}
    </>
  );
}
